import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, AlertTriangle, XCircle, HelpCircle, Clock } from 'lucide-react';

// Define the possible status strings
type Status = "Operational" | "Degraded Performance" | "Partial Outage" | "Major Outage" | "Unknown";

interface ServiceStatusCardProps {
  name: string;
  status: Status;
  last_checked: string;
}

const ServiceStatusCard: React.FC<ServiceStatusCardProps> = ({ name, status, last_checked }) => {
  const getStatusConfig = () => {
    switch (status) {
      case "Operational":
        return {
          icon: CheckCircle,
          color: 'from-emerald-500 to-green-600',
          bg: 'bg-emerald-50/80 dark:bg-emerald-900/20',
          border: 'border-emerald-200 dark:border-emerald-800',
          text: 'text-emerald-800 dark:text-emerald-200',
          statusText: 'text-emerald-600 dark:text-emerald-400',
          glow: 'shadow-emerald-500/20',
        };
      case "Degraded Performance":
        return {
          icon: AlertTriangle,
          color: 'from-amber-500 to-orange-600',
          bg: 'bg-amber-50/80 dark:bg-amber-900/20',
          border: 'border-amber-200 dark:border-amber-800',
          text: 'text-amber-800 dark:text-amber-200',
          statusText: 'text-amber-600 dark:text-amber-400',
          glow: 'shadow-amber-500/20',
        };
      case "Partial Outage":
        return {
          icon: AlertTriangle,
          color: 'from-orange-500 to-red-600',
          bg: 'bg-orange-50/80 dark:bg-orange-900/20',
          border: 'border-orange-200 dark:border-orange-800',
          text: 'text-orange-800 dark:text-orange-200',
          statusText: 'text-orange-600 dark:text-orange-400',
          glow: 'shadow-orange-500/20',
        };
      case "Major Outage":
        return {
          icon: XCircle,
          color: 'from-red-500 to-red-700',
          bg: 'bg-red-50/80 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          text: 'text-red-800 dark:text-red-200',
          statusText: 'text-red-600 dark:text-red-400',
          glow: 'shadow-red-500/20',
        };
      default:
        return {
          icon: HelpCircle,
          color: 'from-slate-500 to-slate-600',
          bg: 'bg-slate-50/80 dark:bg-slate-800/50',
          border: 'border-slate-200 dark:border-slate-700',
          text: 'text-slate-800 dark:text-slate-200',
          statusText: 'text-slate-600 dark:text-slate-400',
          glow: 'shadow-slate-500/20',
        };
    }
  };

  const config = getStatusConfig();
  const StatusIcon = config.icon;
  const formattedDate = new Date(last_checked).toLocaleString();

  return (
    <motion.div 
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className={`glass backdrop-blur-sm rounded-2xl p-6 border ${config.border} ${config.bg} shadow-lg hover:shadow-xl transition-all duration-300 ${config.glow}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className={`text-lg font-bold ${config.text} mb-1`}>
            {name}
          </h3>
          <div className="flex items-center space-x-2">
            <div className={`p-1.5 bg-gradient-to-r ${config.color} rounded-lg shadow-md`}>
              <StatusIcon className="w-4 h-4 text-white" />
            </div>
            <span className={`text-sm font-semibold ${config.statusText}`}>
              {status}
            </span>
          </div>
        </div>
        
        {/* Status indicator dot */}
        <div className="flex flex-col items-end">
          <motion.div 
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className={`w-3 h-3 rounded-full bg-gradient-to-r ${config.color} shadow-lg`}
          />
        </div>
      </div>

      {/* Footer */}
      <div className={`pt-4 border-t ${config.border} space-y-1`}>
        <div className="flex items-center space-x-2 text-xs">
          <Clock className={`w-3 h-3 ${config.statusText}`} />
          <span className={`${config.text} opacity-75`}>
            Last checked: {formattedDate}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default ServiceStatusCard;
